/**
 * Sport Capability Matrix
 *
 * Single source of truth for per-sport feature availability.
 * Consumed by: navConfig.js, SportContext, page components.
 *
 * Rules:
 * - tacticsEnabled: football + football_american only
 * - playbookEnabled: football_american only
 * - All sports share: agenda, training, analytics, library, recruiter, research
 */

export const SPORTS = [
  { key: 'football',          label: 'Futebol',              icon: '⚽' },
  { key: 'football_american', label: 'Futebol Americano',    icon: '🏈' },
  { key: 'swimming',          label: 'Natação',              icon: '🏊' },
  { key: 'athletics',         label: 'Atletismo',            icon: '🏃‍♂️' },
  { key: 'triathlon',         label: 'Triathlon',            icon: '🏃' },
];

/**
 * Per-sport capability map.
 * `modules` lists which OS modules are available.
 * Boolean flags control nav items and page rendering.
 */
export const CAPABILITIES = {
  football: {
    modules: {
      agenda: true,
      training: true,
      analytics: true,
      library: true,
      tactics: true,
      recruiter: true,
      research: true,
    },
    tacticsEnabled: true,
    playbookEnabled: false,
  },

  football_american: {
    modules: {
      agenda: true,
      training: true,
      analytics: true,
      library: true,
      tactics: true,
      recruiter: true,
      research: true,
    },
    tacticsEnabled: true,
    playbookEnabled: true,
  },

  swimming: {
    modules: {
      agenda: true,
      training: true,
      analytics: true,
      library: true,
      tactics: false,
      recruiter: true,
      research: true,
    },
    tacticsEnabled: false,
    playbookEnabled: false,
  },

  athletics: {
    modules: {
      agenda: true,
      training: true,
      analytics: true,
      library: true,
      tactics: false,
      recruiter: true,
      research: true,
    },
    tacticsEnabled: false,
    playbookEnabled: false,
    trackVisionEnabled: true,
  },

  triathlon: {
    modules: {
      agenda: true,
      training: true,
      analytics: true,
      library: true,
      tactics: false,
      recruiter: true,
      research: true,
    },
    tacticsEnabled: false,
    playbookEnabled: false,
  },
};

/**
 * Helper: get capability object for a sport key.
 * Returns a safe default when sportKey is null/unknown.
 */
export const getCapabilities = (sportKey) => {
  if (!sportKey || !CAPABILITIES[sportKey]) {
    // Default: show everything (graceful degradation)
    return {
      modules: {
        agenda: true,
        training: true,
        analytics: true,
        library: true,
        tactics: true,
        recruiter: true,
        research: true,
      },
      tacticsEnabled: true,
      playbookEnabled: true,
    };
  }
  return CAPABILITIES[sportKey];
};

/**
 * Helper: check a single capability.
 * Usage: hasCap('football_american', 'playbookEnabled') => true
 */
export const hasCap = (sportKey, capName) => {
  const caps = getCapabilities(sportKey);
  return !!caps[capName];
};
